import { Breadcrumb, Button, Card, Col, Empty, Flex, Row, Skeleton, Tag, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useParams } from "react-router-dom"

import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { empty } from "../../../constants/placeholders.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import * as services from "../api/services.ts"
import type { CreateSupplierData } from "../api/services.types.ts"
import { SupplierFormModal } from "../components/SupplierFormModal.tsx"
import { SupplierMaterialsCard } from "../components/SupplierMaterials/SupplierMaterialsCard.tsx"
import { updateSupplier } from "../redux/suppliers.thunks.ts"
import type { SupplierListItem } from "../types/types.ts"

const { Title, Text } = Typography

const DetailField = ({
  label,
  value
}: {
  label: string
  value?: React.ReactNode
}) => (
  <Col xs={24} md={12}>
    <Flex vertical gap={2}>
      <Text type="secondary" className="text-xs">
        {label}
      </Text>
      <Text>{value || empty}</Text>
    </Flex>
  </Col>
)

const contactName = (supplier: SupplierListItem) =>
  [supplier.first_name, supplier.middle_name, supplier.last_name]
    .filter((part) => part && part.trim())
    .join(" ")

const SupplierDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const [supplier, setSupplier] = useState<SupplierListItem | null>(null)
  const [fetching, setFetching] = useState(true)
  // Only a 404 means the record is gone; a 500 or a dropped connection is a
  // failure to load, which the user can retry.
  const [loadError, setLoadError] = useState<"notFound" | "failed" | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [editing, setEditing] = useState(false)

  const permissions = useAppSelector(
    (state) => state?.auth?.permissions?.codes?.suppliers?.suppliers
  )
  const saving = useAppSelector((state) => state.suppliers.saveFetching)
  const canEdit = !!permissions?.supplier_edit
  const canDelete = !!permissions?.supplier_delete

  useEffect(() => {
    generateDocumentTitle("Supplier")
  }, [])

  useEffect(() => {
    if (!id) return

    let cancelled = false
    // Never show the previous record while another one loads.
    setSupplier(null)
    setFetching(true)
    setLoadError(null)

    services.getSupplierDetails(id).then((response) => {
      if (cancelled) return

      if (!response.ok) {
        setLoadError(response.status === 404 ? "notFound" : "failed")
        setFetching(false)
        return
      }

      setSupplier(response.data as SupplierListItem)
      setFetching(false)
    })

    return () => {
      cancelled = true
    }
  }, [id, reloadKey])

  const handleSave = async (values: CreateSupplierData) => {
    if (!id) return

    const result = await dispatch(updateSupplier({ ...values, id }))
    if (!updateSupplier.fulfilled.match(result)) return

    // `name` is the concatenation the serializer writes from the name parts,
    // so the saved record is the only thing that knows what it became.
    const refreshed = await services.getSupplierDetails(id)
    if (refreshed.ok) setSupplier(refreshed.data as SupplierListItem)
    setEditing(false)
  }

  if (fetching && !supplier) {
    return (
      <Card size="small">
        <Skeleton active paragraph={{ rows: 8 }} />
      </Card>
    )
  }

  if (loadError === "failed") {
    return (
      <Card size="small">
        <Empty description={t("supplierDetail.loadError")}>
          <Button onClick={() => setReloadKey((key) => key + 1)}>
            {t("supplierDetail.retry")}
          </Button>
        </Empty>
      </Card>
    )
  }

  if (!supplier || loadError === "notFound") {
    return (
      <Card size="small">
        <Empty description={t("supplierDetail.notFound")} />
      </Card>
    )
  }

  return (
    <Flex vertical gap={16}>
      <Card size="small">
        <Flex vertical gap={8}>
          <Breadcrumb
            items={[
              {
                title: (
                  <Link to={RoutesPaths.suppliers.list}>
                    {t("suppliers.title")}
                  </Link>
                )
              },
              { title: supplier.name }
            ]}
          />
          <Flex justify="space-between" align="flex-start" wrap gap={16}>
            <Flex align="center" gap={10}>
              <Title level={4} className="!mb-0">
                {supplier.name}
              </Title>
              <Tag>{t("supplierDetail.badge")}</Tag>
            </Flex>
            {canEdit && (
              <Button type="primary" onClick={() => setEditing(true)}>
                {t("supplierDetail.edit")}
              </Button>
            )}
          </Flex>
        </Flex>
      </Card>

      <Card size="small" title={t("supplierDetail.details")}>
        <Row gutter={[12, 12]}>
          <DetailField
            label={t("supplierDetail.contact")}
            value={contactName(supplier)}
          />
          <DetailField
            label={t("suppliers.columns.email")}
            value={supplier.email}
          />
          <DetailField
            label={t("suppliers.columns.phone")}
            value={supplier.phone}
          />
          <DetailField
            label={t("suppliers.columns.address")}
            value={supplier.address}
          />
          <DetailField
            label={t("suppliers.form.notes")}
            value={supplier.notes}
          />
        </Row>
      </Card>

      <SupplierMaterialsCard
        supplierId={supplier.id}
        supplierName={supplier.name}
        canEdit={canEdit}
        canDelete={canDelete}
      />

      {editing && (
        <SupplierFormModal
          saving={saving}
          initialValues={supplier}
          onSave={handleSave}
          onCancel={() => setEditing(false)}
        />
      )}
    </Flex>
  )
}

export default SupplierDetailPage
